import { Button, LoadingBlock } from '@orobix/orobix-fe-components'
import cx from 'classnames'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useParams } from 'react-router-dom'

import Annotator from '../../components/Annotator/Annotator'
import Observations from '../../components/Observations/Observations'
import ObservationsList from '../../components/ObservationsList/ObservationsList'
import { useStore } from '../../store/store'
import { AnnotationType } from '../../types/annotation'
import PromoteAnnotationButton from './PromoteAnnotationButton/PromoteAnnotationButton'
import useStyles from './TrainingTestObservations.styles'

interface RouteParams {
  trainingTestId: string
}

interface TrainingTestObservationsProps {
  annotatorTypeGT: AnnotationType
  annotatorTypeNN: AnnotationType
}

const TrainingTestObservations = ({
  annotatorTypeGT,
  annotatorTypeNN,
}: TrainingTestObservationsProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { trainingTestId } = useParams<RouteParams>()
  const { product } = useStore()

  const [annotationSource, setAnnotationSource] = useState<'GT' | 'NN'>('NN')

  const isAnnotationNN = annotationSource === 'NN'
  const isAnnotationGT = annotationSource === 'GT'

  const [currentObservationId, setCurrentObservationId] = useState<
    number | undefined
  >(undefined)

  return product ? (
    <div className={classes.container}>
      <div className={classes.testOutcomes}>
        <div
          className={cx(
            classes.list,
            currentObservationId && classes.stackedList
          )}
        >
          <Observations params={{ trainingTestId: Number(trainingTestId) }}>
            {({ observations, hasMore, loadMore }) => (
              <InfiniteScroll
                hasMore={hasMore}
                loadMore={loadMore}
                loader={<LoadingBlock message={t('Loading')} key="loading" />}
                useWindow={false}
              >
                {observations ? (
                  <div className={classes.listContent}>
                    <ObservationsList
                      observations={observations.results || []}
                      onItemClick={({ observation }) => {
                        setAnnotationSource('NN')
                        setCurrentObservationId(observation.id)
                      }}
                      currentObservationId={currentObservationId}
                    />
                  </div>
                ) : (
                  <div />
                )}
              </InfiniteScroll>
            )}
          </Observations>
        </div>

        {currentObservationId && (
          <div className={classes.detail}>
            <div className={classes.actions}>
              <div className={classes.switchAnnotation}>
                <Button
                  onClick={() => setAnnotationSource('GT')}
                  label={t('Original annotation')}
                  buttonType="button"
                  size="sm"
                  type={isAnnotationGT ? 'action' : 'default'}
                  disabled={isAnnotationGT}
                />

                <Button
                  onClick={() => setAnnotationSource('NN')}
                  label={t('Prediction')}
                  buttonType="button"
                  size="sm"
                  type={isAnnotationNN ? 'action' : 'default'}
                  disabled={isAnnotationNN}
                />
              </div>

              {isAnnotationNN && (
                <PromoteAnnotationButton
                  trainingTestId={Number(trainingTestId)}
                  observationId={currentObservationId}
                />
              )}
            </div>

            <div className={classes.annotator}>
              <Annotator
                params={{
                  observationId: currentObservationId,
                  trainingTestId: Number(trainingTestId),
                  annotationSource,
                }}
                productId={product.id}
                mode="viewing"
                annotationType={
                  isAnnotationNN ? annotatorTypeNN : annotatorTypeGT
                }
                noSkip
                selectedObservationId={currentObservationId}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null
}

export default TrainingTestObservations
